import React from "react";

import {
  DynamicPropertyResponse,
  PropertyMetadata,
} from "../../api-models/response/DynamicProperty";
import { IssueResponse } from "../../api-models/response/IssueResponse";
import Svg from "../svg/Svg";
import { Info, Warning, Error } from "../svg/svgImageList";
import { translationService } from "../../services/translation/TranslationService";
import { IssueSeverity, PropertyType } from "../../generated/SewerObjects";

const longTextCharacterCount: number = 2000;

export class DynamicProperty implements DynamicPropertyResponse {
  initialValue: string = "";
  value: string;
  metadata: PropertyMetadata;
  issues: IssueResponse[];

  constructor(property: DynamicPropertyResponse) {
    this.value = property.value;
    this.initialValue = property.value;
    this.metadata = property.metadata;
    this.issues = property.issues;
  }

  getValue = () => {
    return this.value;
  };

  getLabel = () => {
    return this.metadata.name;
  };

  getLabelWithUnitOfMeasure = () => {
    return `${this.metadata.name}  (${this.metadata.unit})`;
  };

  hasUnitOfMeasure = () => {
    return this.metadata.unit;
  };

  hasCatalog = () => {
    return this.metadata.catalog !== null;
  };

  getCatalogValue = () => {
    const catalog =
      this.metadata.catalog &&
      this.metadata.catalog.find(
        (o) => o.key.trim() === (this.value ? this.value.trim() : this.value)
      );
    if (catalog) {
      return catalog.value;
    }
  };

  getCatalogOptions = () => {
    if (this.metadata.catalog) {
      return this.metadata.catalog;
    }
    return [];
  };

  getLabelText = () => {
    if (this.isVideoPositionTime()) {
      return `${this.getLabel()} (${translationService.translate("general.minutes-seconds")})`;
    }
    if (this.isTime()) {
      return `${this.getLabel()} (${translationService.translate(
        "general.hours-minutes-seconds"
      )})`;
    }
    if (this.hasUnitOfMeasure()) {
      return this.getLabelWithUnitOfMeasure();
    } else {
      return this.getLabel();
    }
  };

  getIssueIcon() {
    if (!this.issues.length) return undefined;

    const issue = this.issues[0];
    if (issue.severity === IssueSeverity.Info)
      return (
        <Svg svg={Info} title={issue.message} className="severity-info" width="18" height="18" />
      );
    if (issue.severity === IssueSeverity.Warning)
      return (
        <Svg
          svg={Warning}
          title={issue.message}
          className="severity-warning"
          width="18"
          height="18"
        />
      );
    if (issue.severity === IssueSeverity.Error)
      return (
        <Svg svg={Error} title={issue.message} className="severity-error" width="17" height="17" />
      );
  }

  isOfTypeString = () => {
    return this.metadata.type === PropertyType.String && this.metadata.formatString !== 'D2';
  };

  isOfTypeInteger = () => {
    return this.metadata.type === PropertyType.Integer ||
      (this.metadata.type === PropertyType.String &&
      this.metadata.formatString === 'D2' &&
      this.metadata.precision === 0);
  };

  isOfTypeDecimal = () => {
    return this.metadata.type === PropertyType.Decimal ||
      (this.metadata.type === PropertyType.String &&
      this.metadata.formatString === 'D2' &&
      this.metadata.precision !== 0);
  };

  isOfTypeDouble = () => {
    return this.metadata.type === PropertyType.Double;
  };

  isOfTypeBoolean = () => {
    return this.metadata.type === PropertyType.Boolean;
  };

  isOfTypeDate = () => {
    return this.metadata.type === PropertyType.Date;
  };

  isOfTypeYear = () => {
    return this.metadata.type === PropertyType.Year;
  };

  isOfTypeYearAndMonth = () => {
    return this.metadata.type === PropertyType.YearMonth;
  };

  isRequired = () => {
    return this.metadata.required;
  };

  isEditable = () => {
    return !this.metadata.readonly;
  };

  isDate = () => {
    return (
      !this.hasCatalog() &&
      (this.isOfTypeDate() || this.isOfTypeYear() || this.isOfTypeYearAndMonth())
    );
  };

  isString = () => {
    return !this.hasCatalog() && this.isOfTypeString();
  };

  isDropdown = () => {
    return this.hasCatalog();
  };

  isNumericInteger = () => {
    return !this.hasCatalog() && this.isOfTypeInteger();
  };

  isNumericDecimal = () => {
    return !this.hasCatalog() && (this.isOfTypeDecimal() || this.isOfTypeDouble());
  };

  isBoolean = () => {
    return !this.hasCatalog() && this.isOfTypeBoolean();
  };

  isTime = () => {
    return this.metadata.type === PropertyType.Time;
  };

  isText = () => {
    return this.metadata.length >= longTextCharacterCount && this.isOfTypeString();
  };

  isVideoPositionTime = () => {
    return this.metadata.type === PropertyType.VideoPositionTime;
  };

  markAsReadonly() {
    this.metadata.readonly = true;
  }

  markAsEditable() {
    this.metadata.readonly = false;
  }

  setUnit(unit: string) {
    this.metadata.unit = unit;
  }

  setDescription(descriptionToSet: string) {
    this.metadata.description = descriptionToSet;
  }

  setValue(newValue: string) {
    this.value = newValue;
  }
}
